import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Card } from '../../ui/Card/Card'
import { Spinner } from '../../ui/Spinner/Spinner'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import {
  onboardingSchedulingQueryKey,
  onboardingSchedulingQueryOptions,
  onboardingStatusQueryKey,
  onboardingStatusQueryOptions,
} from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import { hasHealthySchedulingSnapshot } from './onboarding-scheduling-contracts'
import { SchedulingSummary } from './SchedulingSummary'
import styles from './SchedulingPage.module.css'

type Feedback =
  | 'invalid'
  | 'no-slots'
  | 'pending'
  | 'stale'
  | 'unavailable'
  | 'validated'
  | null

export function SchedulingBookingPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') {
    return <Spinner label="Restoring your setup" />
  }
  return (
    <AuthenticatedBookingPage
      key={auth.user.uid}
      client={client}
      user={auth.user}
    />
  )
}

function AuthenticatedBookingPage({
  client,
  user,
}: {
  client: OnboardingApiClient
  user: DashboardUser
}) {
  const auth = useAuth()
  const location = useLocation()
  const statusQuery = useQuery(onboardingStatusQueryOptions(client, user))
  if (statusQuery.isPending) return <Spinner label="Checking your setup" />
  if (statusQuery.isError) {
    if (
      statusQuery.error instanceof OnboardingApiError &&
      statusQuery.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    return (
      <StatePanel
        kind="error"
        title="Booking setup could not be loaded"
        description="Retry to load your authoritative onboarding status."
      />
    )
  }
  const destination = routeOnboardingResponse(statusQuery.data)
  if (destination !== 'scheduling') {
    return <Navigate to={destinationPath(destination)} replace />
  }
  return <BookingEditor client={client} user={user} onSignOut={auth.logout} />
}

function BookingEditor({
  client,
  user,
  onSignOut,
}: {
  client: OnboardingApiClient
  user: DashboardUser
  onSignOut: () => Promise<void>
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const query = useQuery(onboardingSchedulingQueryOptions(client, user))
  const [consent, setConsent] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<number | null>(null)
  const [initializedRevision, setInitializedRevision] = useState<number | null>(
    null
  )
  const [working, setWorking] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [feedback, setFeedback] = useState<Feedback>(null)

  useEffect(() => {
    if (query.data && initializedRevision === null) {
      setInitializedRevision(query.data.revision)
      setConsent(query.data.bookingConsent.newAppointment)
      setSelectedProvider(
        query.data.schemaVersion === 1
          ? (query.data.defaultProviderId ?? null)
          : null
      )
    }
  }, [initializedRevision, query.data])

  if (query.isPending) return <Spinner label="Loading booking setup" />
  if (query.isError) {
    return (
      <Card className={styles.panel}>
        <StatePanel
          kind="error"
          title="Booking setup could not be loaded"
          description="Retry to restore the latest saved consent and provider selection."
        />
        <Button type="button" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </Card>
    )
  }

  if (initializedRevision === null)
    return <Spinner label="Restoring booking setup" />

  if (query.data.schemaVersion === 2) {
    return <Navigate to="/onboarding/scheduling" replace />
  }
  const scheduling = query.data

  if (!hasHealthySchedulingSnapshot(scheduling)) {
    return <Navigate to="/onboarding/scheduling" replace />
  }

  const snapshot = scheduling.snapshot
  if (!snapshot) return <Navigate to="/onboarding/scheduling" replace />
  const usableProviders = snapshot.providers.filter(
    (provider) => provider.usable
  )
  const needsProvider = consent && usableProviders.length > 1
  const validationPassed = [
    'VALID_QUERY_NO_SLOTS',
    'VALID_QUERY_WITH_SLOTS',
  ].includes(scheduling.validation.status)

  const restoreLatest = async () => {
    const refreshed = await query.refetch()
    if (refreshed.data?.schemaVersion === 1) {
      setConsent(refreshed.data.bookingConsent.newAppointment)
      setSelectedProvider(refreshed.data.defaultProviderId ?? null)
      setInitializedRevision(refreshed.data.revision)
      setDirty(false)
    }
  }

  const validateSetup = async () => {
    if (working || (needsProvider && selectedProvider === null)) return
    setWorking(true)
    setFeedback(null)
    try {
      const patch = {
        bookingConsent: { newAppointment: consent },
        ...(needsProvider && selectedProvider !== null
          ? { defaultProviderId: selectedProvider }
          : {}),
      }
      const saved = await client.patchScheduling(
        user,
        scheduling.revision,
        scheduling.discoveryRevision,
        patch
      )
      queryClient.setQueryData(onboardingSchedulingQueryKey(user.uid), saved)
      if (saved.schemaVersion !== 1) throw new Error('scheduling mode changed')
      const validated = await client.validateScheduling(
        user,
        saved.revision,
        saved.discoveryRevision
      )
      if (validated.schemaVersion !== 1)
        throw new Error('scheduling mode changed')
      queryClient.setQueryData(
        onboardingSchedulingQueryKey(user.uid),
        validated
      )
      setDirty(false)
      setInitializedRevision(validated.revision)
      if (validated.validation.status === 'VALID_QUERY_WITH_SLOTS') {
        setFeedback('validated')
      } else if (validated.validation.status === 'VALID_QUERY_NO_SLOTS') {
        setFeedback('no-slots')
      } else {
        setFeedback('invalid')
      }
    } catch (error) {
      if (error instanceof OnboardingApiError) {
        if (error.code === 'operation-in-progress') setFeedback('pending')
        else if (
          error.code === 'revision-conflict' ||
          error.code === 'stale-discovery'
        )
          setFeedback('stale')
        else setFeedback('unavailable')
      } else {
        setFeedback('unavailable')
      }
      await restoreLatest()
    } finally {
      setWorking(false)
    }
  }

  const complete = async () => {
    if (working || dirty || !validationPassed || !scheduling.completionReady)
      return
    setWorking(true)
    setFeedback(null)
    try {
      const completed = await client.completeScheduling(
        user,
        scheduling.revision
      )
      queryClient.setQueryData(
        onboardingSchedulingQueryKey(user.uid),
        completed
      )
      await queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryKey(user.uid),
        refetchType: 'none',
      })
      const status = await queryClient.fetchQuery(
        onboardingStatusQueryOptions(client, user)
      )
      await navigate(destinationPath(routeOnboardingResponse(status)), {
        replace: true,
      })
    } catch (error) {
      setFeedback(
        error instanceof OnboardingApiError &&
          (error.code === 'revision-conflict' ||
            error.code === 'stale-discovery')
          ? 'stale'
          : 'unavailable'
      )
      await restoreLatest()
    } finally {
      setWorking(false)
    }
  }

  const changeConsent = (next: boolean) => {
    setConsent(next)
    setDirty(
      next !== scheduling.bookingConsent.newAppointment ||
        selectedProvider !== (scheduling.defaultProviderId ?? null)
    )
    setFeedback(null)
  }
  const changeProvider = (next: number) => {
    setSelectedProvider(next)
    setDirty(
      consent !== scheduling.bookingConsent.newAppointment ||
        next !== (scheduling.defaultProviderId ?? null)
    )
    setFeedback(null)
  }

  const currentDefault = usableProviders.find(
    (provider) => provider.id === scheduling.defaultProviderId
  )
  return (
    <Card className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.eyebrow}>Booking setup</span>
        <h1>Choose how Flio may book</h1>
        <p>
          Your NexHealth/PMS system remains the source of truth for schedules
          and appointment durations.
        </p>
      </div>

      <SchedulingSummary scheduling={scheduling} />

      <fieldset className={styles.consent} disabled={working}>
        <legend>Allow Flio to book new appointments</legend>
        <label>
          <input
            type="radio"
            name="new-appointment-consent"
            checked={consent}
            onChange={() => changeConsent(true)}
          />
          <span>
            <strong>On</strong>
            Flio may create new appointments through supported booking
            workflows. Availability still depends on NexHealth/PMS.
          </span>
        </label>
        <label>
          <input
            type="radio"
            name="new-appointment-consent"
            checked={!consent}
            onChange={() => changeConsent(false)}
          />
          <span>
            <strong>Off</strong>
            Flio will not create new appointments. The scheduling connection can
            still be validated.
          </span>
        </label>
      </fieldset>

      {consent && usableProviders.length === 1 && (
        <p className={styles.defaultProvider}>
          Default provider: {usableProviders[0]?.name}
        </p>
      )}
      {!consent && currentDefault && (
        <p className={styles.defaultProvider}>
          Available provider: {currentDefault.name}. No selection is required
          while booking is off.
        </p>
      )}
      {needsProvider && (
        <label className={styles.providerSelect}>
          Default provider
          <select
            value={selectedProvider ?? ''}
            disabled={working}
            onChange={(event) => changeProvider(Number(event.target.value))}
          >
            <option value="">Choose a provider</option>
            {usableProviders.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {feedback === 'validated' && (
        <StatePanel
          title="Scheduling validated"
          description="Flio found a valid read-only scheduling query. Complete this step when ready."
        />
      )}
      {feedback === 'no-slots' && (
        <StatePanel
          title="Scheduling connected — no current openings"
          description="The read-only query is valid, but no openings were found in the current window. This does not block completion."
        />
      )}
      {feedback === 'invalid' && (
        <StatePanel
          kind="error"
          title="Scheduling configuration needs attention"
          description="Review provider availability and appointment types in NexHealth/PMS, then refresh the scheduling check."
        />
      )}
      {feedback === 'pending' && (
        <StatePanel
          title="Validation is already running"
          description="Wait a moment, then retry to restore its authoritative result."
        />
      )}
      {feedback === 'stale' && (
        <StatePanel
          kind="error"
          title="Scheduling information changed"
          description="The latest saved choices were restored. Review them before validating again."
        />
      )}
      {feedback === 'unavailable' && (
        <StatePanel
          kind="error"
          title="NexHealth is temporarily unavailable"
          description="Your saved choices remain safe. Retry validation when the service is available."
        />
      )}

      <p className={styles.note}>
        Clinic hours define when Flio may offer appointments. They do not create
        bookable slots by themselves.
      </p>
      <div className={styles.actions}>
        {validationPassed && !dirty && scheduling.completionReady ? (
          <Button
            type="button"
            disabled={working}
            onClick={() => void complete()}
          >
            {working ? 'Completing…' : 'Complete scheduling'}
          </Button>
        ) : (
          <Button
            type="button"
            disabled={working || (needsProvider && selectedProvider === null)}
            onClick={() => void validateSetup()}
          >
            {working ? 'Validating…' : 'Validate scheduling'}
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void navigate('/onboarding/scheduling')}
        >
          Refresh scheduling data
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={working}
          onClick={() => void onSignOut()}
        >
          Sign out
        </Button>
      </div>
    </Card>
  )
}
